/**
 * FinancialSummaryCards — 4 KPI cards across the top of the Finance dashboard.
 *
 * 1. Total Investment (iR&D + fR&D sum with breakdown)
 * 2. Revenue Projection (from resourceConfig, R&D as % of revenue)
 * 3. Resource Gap (fteDelta, red/green indicator)
 * 4. Portfolio ROI (average ROI across nodes with data)
 */

import { useMemo } from 'react';
import { DollarSign, TrendingUp, Users, Percent, Calculator } from 'lucide-react';
import type { AdjacencyNode } from '../../types/adjacency';
import type { ResourceConfig } from '../../types/resource-config';
import { computeResourceMetrics } from '../../types/resource-config';
import { computeFinancialMetrics, formatCompactCurrency } from '../../utils/financial-calculations';

interface Props {
  nodes: AdjacencyNode[];
  resourceConfig: ResourceConfig;
  discountRate: number;
  periodYears: number;
}

export function FinancialSummaryCards({ nodes, resourceConfig, discountRate, periodYears }: Props) {
  const stats = useMemo(() => {
    let totalIRnD = 0;
    let totalFRnD = 0;
    let roiSum = 0;
    let roiCount = 0;

    let npvSum = 0;
    let npvCount = 0;

    for (const n of nodes) {
      totalIRnD += n.iRnD ?? 0;
      totalFRnD += n.fRnD ?? 0;
      const fm = computeFinancialMetrics(n, discountRate, periodYears);
      if (fm.roi != null) {
        roiSum += fm.roi;
        roiCount++;
      }
      if (fm.npv != null) {
        npvSum += fm.npv;
        npvCount++;
      }
    }

    // iR&D + fR&D is the single source of truth for investment
    const grandTotal = totalIRnD + totalFRnD;
    const metrics = computeResourceMetrics(resourceConfig, grandTotal);
    const avgRoi = roiCount > 0 ? roiSum / roiCount : null;

    return { totalIRnD, totalFRnD, grandTotal, metrics, avgRoi, roiCount, npvSum, npvCount };
  }, [nodes, resourceConfig, discountRate, periodYears]);

  const rndPct = stats.grandTotal > 0 && resourceConfig.revenueProjection > 0
    ? ((stats.grandTotal / resourceConfig.revenueProjection) * 100).toFixed(1)
    : '—';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
      {/* 1. Total Investment */}
      <div className="glass-panel p-4 rounded-lg flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: '#3b82f620' }}
        >
          <DollarSign size={20} style={{ color: '#3b82f6' }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>
            Total Investment
          </div>
          <div className="text-xl font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>
            {formatCompactCurrency(stats.grandTotal)}
          </div>
          <div className="text-xs mt-0.5 truncate tabular-nums" style={{ color: 'var(--text-dim)' }}>
            iR&D {formatCompactCurrency(stats.totalIRnD)} · FR&D {formatCompactCurrency(stats.totalFRnD)}
          </div>
        </div>
      </div>

      {/* 2. Revenue Projection */}
      <KpiCard
        icon={TrendingUp}
        iconColor="#22c55e"
        title="Revenue Projection"
        value={formatCompactCurrency(resourceConfig.revenueProjection)}
        subtitle={`R&D = ${rndPct}% of Revenue`}
      />

      {/* 3. Resource Requirement */}
      <KpiCard
        icon={Users}
        iconColor={stats.metrics.fteDelta >= 0 ? '#22c55e' : '#ef4444'}
        title="Resource Requirement"
        value={`${stats.metrics.fteDelta >= 0 ? '+' : ''}${stats.metrics.fteDelta.toFixed(1)} FTE`}
        subtitle={`Need ${stats.metrics.ftesNeeded.toFixed(1)} / Have ${stats.metrics.totalAvailableFtes.toFixed(1)}`}
        valueColor={stats.metrics.fteDelta >= 0 ? '#22c55e' : '#ef4444'}
      />

      {/* 4. Portfolio ROI */}
      <KpiCard
        icon={Percent}
        iconColor="#d97706"
        title="Portfolio ROI"
        value={stats.avgRoi != null ? `${stats.avgRoi.toFixed(1)}%` : '—'}
        subtitle={stats.roiCount > 0 ? `Avg across ${stats.roiCount} projects` : 'No revenue data'}
      />

      {/* 5. Portfolio NPV */}
      <KpiCard
        icon={Calculator}
        iconColor={stats.npvSum >= 0 ? '#22c55e' : '#ef4444'}
        title="Portfolio NPV"
        value={stats.npvCount > 0 ? formatCompactCurrency(stats.npvSum) : '—'}
        subtitle={stats.npvCount > 0 ? `${stats.npvCount} projects · ${discountRate * 100}% / ${periodYears}yr` : 'No revenue data'}
        valueColor={stats.npvCount > 0 ? (stats.npvSum >= 0 ? '#22c55e' : '#ef4444') : undefined}
      />
    </div>
  );
}

function KpiCard({
  icon: Icon,
  iconColor,
  title,
  value,
  subtitle,
  valueColor,
}: {
  icon: typeof DollarSign;
  iconColor: string;
  title: string;
  value: string;
  subtitle: string;
  valueColor?: string;
}) {
  return (
    <div className="glass-panel p-4 rounded-lg flex items-start gap-3">
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: iconColor + '20' }}
      >
        <Icon size={20} style={{ color: iconColor }} />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-medium mb-0.5" style={{ color: 'var(--text-muted)' }}>
          {title}
        </div>
        <div
          className="text-xl font-bold tabular-nums truncate"
          style={{ color: valueColor ?? 'var(--text-primary)' }}
        >
          {value}
        </div>
        <div className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-dim)' }}>
          {subtitle}
        </div>
      </div>
    </div>
  );
}
